import { AxiosError } from 'axios';
import api, { apiClient } from '../api/apiClient';
import type {
  AvailableSlot,
  AvailableSlotsResponse,
  BlockedDate,
  SlotMetadata,
  TimeSlot,
  WorkingHours,
} from '../types/schedule';
import { debounce } from 'lodash';
import { format } from 'date-fns';

// Re-export types for external use
export { TimeSlot, WorkingHours, BlockedDate, AvailableSlot, SlotMetadata, AvailableSlotsResponse };

/**
 * Response shape for fetching a stylist's schedule.
 */
export interface ScheduleResponse {
  data: {
    defaultHours: WorkingHours[];
    specialDates: Array<{
      date: string;             // ISO date string
      isWorkingDay: boolean;
      shifts: Array<{
        startTime: string;
        endTime: string;
        breakStart?: string;
        breakEnd?: string;
      }>;
      note?: string;
    }>;
    timeOff: Array<{
      startDate: string;
      endDate: string;
      type: 'vacation' | 'sick' | 'personal' | 'holiday' | 'other';
      note?: string;
    }>;
    blockedDates?: BlockedDate[]; // Add this field
  }
}

class ScheduleService {
  private requestQueue = new Map<string, Promise<AvailableSlotsResponse>>();
  private maxRetries = 3;
  private retryDelay = 1000;

  private async executeWithRetry<T>(
    operation: () => Promise<T>,
    retries = this.maxRetries
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      const err = error as AxiosError<any>;
      if (err.response?.status === 429 && retries > 0) {
        await new Promise(r => setTimeout(r, this.retryDelay));
        this.retryDelay *= 2;
        return this.executeWithRetry(operation, retries - 1);
      }
      throw err;
    }
  }

  private getRequestKey(
    stylistId: string,
    date: string,
    serviceId?: string,
    duration?: number
  ): string {
    return `${stylistId}-${date}-${serviceId || ''}-${duration || ''}`;
  }

  private debouncedGetSlots = (
    stylistId: string,
    date: string,
    serviceId?: string,
    duration?: number
  ): Promise<AvailableSlotsResponse> => {
    return new Promise((resolve, reject) => {
      debounce(async () => {
        try {
          console.log('Making slots API request with params:', {
            stylistId,
            date,
            serviceId,
            duration: duration ? Number(duration) : undefined
          });
          
          // Get browser timezone
          const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          
          // Construct the direct URL with query parameters (exactly like the mobile app)
          const params = {
            stylistId,
            date,
            serviceId,
            duration: duration ? Number(duration) : undefined,
            clientTimezone: browserTimezone
          };
          
          console.log('Request URL will be: /schedule/available-slots with params:', params);
          
          // Use apiClient directly to avoid the params nesting issue
          const response = await apiClient.get('/schedule/available-slots', {
            params // This will add params directly, without nesting under 'params'
          });

          console.log('Available slots response:', response.data);
          
          // Handle 304 Not Modified case (which means use cache)
          if (response.status === 304) {
            console.log('Received 304 Not Modified - Using cached data');
          }
          
          // Handle empty or missing response data
          if (!response.data || (Object.keys(response.data).length === 0)) {
            // Create a default response that matches the expected format
            const defaultResponse = {
              success: true,
              data: [], // Empty slots array
              metadata: {
                serviceDuration: duration || 0,
                workingHours: { start: '', end: '' },
                slotInterval: 30,
                reasons: ['NO_SLOTS_AVAILABLE', 'SERVER_ERROR']
              }
            };
            console.log('Using default empty response:', defaultResponse);
            resolve(defaultResponse);
          } else {
            console.log('Using actual response data:', response.data);
            
            // Make sure we're getting the right format - sometimes it might be nested
            if (response.data.data) {
              resolve(response.data);
            } else {
              // If the response data is directly the slots array
              resolve({
                success: true,
                data: Array.isArray(response.data) ? response.data : [],
                metadata: {
                  serviceDuration: duration || 0,
                  workingHours: { start: '', end: '' },
                  slotInterval: 30,
                  reasons: []
                }
              });
            }
          }
        } catch (error) {
          console.error('API request error:', error);
          reject(error);
        }
      }, 500)();
    });
  };

  async getStylistSchedule(
    stylistId: string,
    params?: { startDate?: string; endDate?: string }
  ): Promise<ScheduleResponse['data']> {
    const response = await this.executeWithRetry(() =>
      api.getWithParams<{ success: boolean; data: ScheduleResponse['data'] }>(
        `/schedule/stylist/${stylistId}`,
        { params }
      )
    );
    return response.data.data;
  }

  async updateWorkingHours(
    workingHours: WorkingHours[]
  ): Promise<ScheduleResponse> {
    const response = await this.executeWithRetry(() =>
      api.putWithData<{ data: ScheduleResponse }>(
        '/schedule/working-hours',
        { workingHours }
      )
    );
    return response.data.data;
  }

  async getAvailableSlots(
    stylistId: string,
    date: string | Date,
    serviceId?: string,
    duration?: number
  ): Promise<AvailableSlotsResponse> {
    console.log('Getting available slots for:', {
      stylistId,
      date: date instanceof Date ? date.toISOString() : date,
      serviceId,
      duration
    });

    // Convert Date object to proper ISO format string
    const dateStr = date instanceof Date ? 
      date.toISOString() : 
      date;
      
    const key = this.getRequestKey(stylistId, dateStr, serviceId, duration);
    
    // Use cached request if available
    const existingRequest = this.requestQueue.get(key);
    if (existingRequest) {
      console.log('Using cached request for:', key);
      return existingRequest;
    }

    const newRequest = this.executeWithRetry(async () => {
      try {
        const response = await this.debouncedGetSlots(stylistId, dateStr, serviceId, duration);
        
        if (!response.data.length) {
          console.log('No available slots found. Checking metadata:', response.metadata);
        } else {
          console.log(`Found ${response.data.length} available slots`);
        }
        
        return response;
      } catch (error) {
        console.error('Error fetching slots:', error);
        throw error;
      }
    });

    this.requestQueue.set(key, newRequest);

    try {
      return await newRequest;
    } finally {
      this.requestQueue.delete(key);
    }
  }

  getAvailabilityMessage(metadata: SlotMetadata): string {
    if (metadata.reasons.includes('NOT_WORKING_DAY')) {
      return 'Stylist is not available on this day';
    }
    if (metadata.reasons.includes('NO_SLOTS_AVAILABLE')) {
      return 'No slots available for this service';
    }
    if (metadata.reasons.includes('LONG_SERVICE_LIMITED_SLOTS')) {
      const hours = Math.floor(metadata.serviceDuration / 60);
      const minutes = metadata.serviceDuration % 60;
      return `This service is ${hours}h ${minutes}m; limited slots available.`;
    }
    return '';
  }

  async setTimeOff(
    data: {
      startDate: string | Date;
      endDate: string | Date;
      type: 'vacation' | 'sick' | 'personal' | 'holiday' | 'other';
      note?: string;
    }
  ): Promise<ScheduleResponse> {
    // Convert Date objects to strings if needed
    const formattedData = {
      ...data,
      startDate: data.startDate instanceof Date ? format(data.startDate, 'yyyy-MM-dd') : data.startDate,
      endDate: data.endDate instanceof Date ? format(data.endDate, 'yyyy-MM-dd') : data.endDate
    };
    
    const response = await this.executeWithRetry(() =>
      api.postWithData<{ data: ScheduleResponse }>(
        '/schedule/time-off',
        formattedData
      )
    );
    return response.data.data;
  }

  async removeTimeOff(timeOffId: string): Promise<ScheduleResponse> {
    const response = await this.executeWithRetry(() =>
      api.deleteWithParams<{ data: ScheduleResponse }>(
        `/schedule/time-off/${timeOffId}`
      )
    );
    return response.data.data;
  }

  // Additional methods needed by the application
  async validateShift(shiftData: any): Promise<boolean> {
    try {
      const response = await api.postWithData<{ valid: boolean }>(
        '/schedule/validate-shift',
        shiftData
      );
      return response.data.valid;
    } catch (error) {
      return false;
    }
  }

  async blockDate(stylistId: string, date: string, reason: string, note?: string): Promise<{ data: { blockedDates: BlockedDate[] } }> {
    const response = await this.executeWithRetry(() =>
      api.postWithData<{ data: { blockedDates: BlockedDate[] } }>(
        '/schedule/block-date',
        { stylistId, date, reason, note }
      )
    );
    return response.data;
  }

  async unblockDate(blockedDateId: string): Promise<{ data: { blockedDates: BlockedDate[] } }> {
    const response = await this.executeWithRetry(() =>
      api.deleteWithParams<{ data: { blockedDates: BlockedDate[] } }>(
        `/schedule/blocked-dates/${blockedDateId}`
      )
    );
    return response.data;
  }

  async getBlockedDates(startDate?: string, endDate?: string): Promise<BlockedDate[]> {
    const response = await this.executeWithRetry(() =>
      api.getWithParams<{ data: BlockedDate[] }>(
        '/schedule/blocked-dates',
        { params: { startDate, endDate } }
      )
    );
    return response.data.data;
  }
}

export const scheduleService = new ScheduleService();
